/**
 * PREFILL KNOWN IDENTITY ON A REPEAT ORDER.
 *
 * A returning customer must not retype what we already hold. The values come
 * from `useRepeatProfile`, whose precedence is PROFILE FIRST and the latest
 * order only as a fallback (see repeat-flow.ts) — so an address changed in My
 * account prefills the next order, never the stale one off the last order.
 *
 * PREFILL ONLY, three rules:
 *  1. It seeds an EMPTY field on the NEW order draft. A field the customer has
 *     already typed on this order is never overwritten.
 *  2. Nothing is written back to the profile, and nothing is written to any
 *     existing order. A confirmed order keeps its own address snapshot; an edit
 *     here reaches only the order being created.
 *  3. It runs ONCE per screen, the first time the stored values resolve, so a
 *     later render can never re-seed over an edit.
 */
import { useEffect, useRef } from "react";
import { useRepeatProfile, type RepeatKyc } from "@/lib/repeat-flow";

export function useKnownDetailsPrefill(
  slug: string,
  apply: (known: RepeatKyc) => void,
): { loading: boolean } {
  const repeat = useRepeatProfile(slug);
  const done = useRef(false);
  const applyRef = useRef(apply);
  applyRef.current = apply;

  useEffect(() => {
    if (done.current || repeat.loading || !repeat.kyc) return;
    done.current = true;
    applyRef.current(repeat.kyc);
  }, [repeat.loading, repeat.kyc]);

  return { loading: repeat.loading };
}
